import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { getToken, getUser, logout } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { ExternalLink, Upload, Trash2, Save, Eye, Plus, Star, Facebook, Instagram, Linkedin, Twitter, Youtube, Music2, Monitor, Tablet, Smartphone, EyeOff, Share2, Copy, Check, Clock, Mail, GripVertical, Receipt, Download, FileText, Briefcase, User, Image as ImageIcon, MapPin, AlertTriangle, ShieldAlert, KeyRound, Sparkles, LayoutGrid, Play, CreditCard } from "lucide-react";
import TemplateRenderer from "../components/templates/TemplateRenderer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/assets/logo.png";

const DEVICES = {
  desktop: { w: 1280, h: 800, label: "Bureau", Icon: Monitor },
  tablet: { w: 820, h: 1100, label: "Tablette", Icon: Tablet },
  mobile: { w: 390, h: 780, label: "Mobile", Icon: Smartphone },
};

// Tabs metadata — content sections of the client dashboard
const DASH_TABS = [
  { value: "sections",     label: "Sections",      hint: "Activer/masquer les blocs",  Icon: LayoutGrid,   testid: "tab-sections" },
  { value: "info",         label: "Identité",      hint: "Nom, SIRET, à propos",      Icon: User,         testid: "tab-info" },
  { value: "contact",      label: "Contact",       hint: "Email, tél, adresse",       Icon: Mail,         testid: "tab-contact" },
  { value: "hours",        label: "Horaires",      hint: "Jours & créneaux",          Icon: Clock,        testid: "tab-hours" },
  { value: "media",        label: "Logo",          hint: "Logo de l'entreprise",      Icon: ImageIcon,    testid: "tab-media" },
  { value: "slideshow",    label: "Diaporama",     hint: "Défilement photos hero",    Icon: Play,         testid: "tab-slideshow" },
  { value: "map",          label: "Plan",          hint: "Carte & itinéraire",        Icon: MapPin,       testid: "tab-map" },
  { value: "testimonials", label: "Témoignages",   hint: "Avis clients",              Icon: Star,         testid: "tab-testimonials" },
  { value: "services",     label: "Services",      hint: "Vos offres",                Icon: Briefcase,    testid: "tab-services" },
  { value: "social",       label: "Réseaux & CTA", hint: "Liens & bouton d'action",   Icon: Share2,       testid: "tab-social" },
  { value: "invoices",     label: "Facture",       hint: "Votre justificatif",        Icon: Receipt,      testid: "tab-invoices" },
  { value: "billing",      label: "Abonnement",    hint: "9,90 €/mois, résiliable",   Icon: CreditCard,   testid: "tab-billing" },
];

const SECTION_ITEMS = [
  { key: "hero",         label: "En-tête / Hero",           desc: "La grande bannière d'accueil (image, titre, boutons)." },
  { key: "about",        label: "Présentation / À propos",  desc: "Le paragraphe décrivant votre activité." },
  { key: "services",     label: "Services",                 desc: "La liste de vos prestations, tarifs ou menu." },
  { key: "testimonials", label: "Avis clients",             desc: "Les témoignages de vos clients." },
  { key: "hours",        label: "Horaires d'ouverture",     desc: "Le tableau récapitulatif des jours & horaires." },
  { key: "contact",      label: "Contact",                  desc: "La section adresse, tél, email & réseaux." },
  { key: "map",          label: "Plan / Carte Google Maps", desc: "L'iframe Google Maps intégrée à la section contact." },
];

const Dashboard = () => {
  const nav = useNavigate();
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLink, setShareLink] = useState(null); // {url, expires_at}
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [socialCopied, setSocialCopied] = useState(false);
  // Drag & drop state for testimonials and services lists
  const [dragState, setDragState] = useState({ list: null, fromIdx: null, overIdx: null });
  // Invoices (Factur-X)
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [sub, setSub] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [billingBusy, setBillingBusy] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  // Delete account flow
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  // Change password flow
  const [me, setMe] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      nav("/mon-compte-client");
      return;
    }
    api.get("/business/me")
      .then((res) => {
        // Backfill stable _uid on existing list items so React keys are stable across removals
        const uidFn = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `uid-${Date.now()}-${Math.random().toString(36).slice(2)}`);
        const data = { ...res.data };
        if (Array.isArray(data.testimonials)) data.testimonials = data.testimonials.map((t) => (t && t._uid ? t : { ...t, _uid: uidFn() }));
        if (Array.isArray(data.services)) data.services = data.services.map((s) => (s && s._uid ? s : { ...s, _uid: uidFn() }));
        setPage(data);
      })
      .catch(() => {
        toast.error("Session expirée");
        logout();
        nav("/mon-compte-client");
      });
  }, [nav]);

  // Load invoices once authenticated
  useEffect(() => {
    if (!getToken()) return;
    let alive = true;
    setInvoicesLoading(true);
    api.get("/me/invoices")
      .then((res) => { if (alive) setInvoices(res.data || []); })
      .catch(() => {})
      .finally(() => { if (alive) setInvoicesLoading(false); });
    return () => { alive = false; };
  }, []);

  // Load subscription state
  const loadSubscription = () => {
    if (!getToken()) return;
    setSubLoading(true);
    api.get("/billing/subscription")
      .then((res) => setSub(res.data))
      .catch(() => {})
      .finally(() => setSubLoading(false));
  };
  useEffect(() => { loadSubscription(); }, []);

  // Retour du checkout d'abonnement (?abonnement=success|cancel)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get("abonnement");
    if (!state) return;
    if (state === "success") {
      toast.success("Abonnement activé. Merci !");
      setTimeout(loadSubscription, 2500);
    } else {
      toast.info("Souscription annulée.");
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const openBillingPortal = async () => {
    setBillingBusy(true);
    try {
      const res = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portail de facturation indisponible");
      setBillingBusy(false);
    }
  };

  const startSubscription = async () => {
    setBillingBusy(true);
    try {
      const res = await api.post("/billing/subscribe", { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Souscription indisponible pour le moment");
      setBillingBusy(false);
    }
  };

  // Load current user (for password_reset_pending flag)
  const refreshMe = () => {
    if (!getToken()) return;
    api.get("/auth/me").then((res) => setMe(res.data)).catch(() => {});
  };
  useEffect(() => { refreshMe(); }, []);
  // Auto-open password dialog on first load if a reset is pending
  useEffect(() => {
    if (me?.password_reset_pending) setPwOpen(true);
  }, [me?.password_reset_pending]);

  const downloadInvoice = async (inv) => {
    setDownloadingInvoiceId(inv.id);
    try {
      const res = await api.get(`/me/invoices/${inv.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = inv.pdf_filename || `facture-${inv.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Facture ${inv.number} téléchargée`);
    } catch (e) {
      toast.error("Échec du téléchargement");
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const deleteAccount = async () => {
    if (deleteConfirm.trim().toUpperCase() !== "SUPPRIMER") {
      toast.error("Veuillez taper SUPPRIMER pour confirmer");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/me/account");
      toast.success("Compte désactivé. Reconnectez-vous quand vous le souhaitez pour le réactiver gratuitement.", { duration: 6000 });
      logout();
      nav("/mon-compte-client");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la suppression");
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
      setDeleteConfirm("");
    }
  };

  const changePassword = async (e) => {
    e?.preventDefault?.();
    if (pwForm.new_password.length < 8) {
      toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error("Les deux nouveaux mots de passe ne correspondent pas.");
      return;
    }
    if (pwForm.new_password === pwForm.current_password) {
      toast.error("Le nouveau mot de passe doit être différent de l'ancien.");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/me/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Mot de passe modifié avec succès.");
      setPwOpen(false);
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
      refreshMe();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la modification du mot de passe.");
    } finally {
      setPwSaving(false);
    }
  };

  if (!page) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500">Chargement…</div>
      </div>
    );
  }

  const upd = (patch) => setPage((p) => ({ ...p, ...patch }));

  const setHour = (idx, patch) => {
    const oh = [...page.opening_hours];
    oh[idx] = { ...oh[idx], ...patch };
    upd({ opening_hours: oh });
  };

  // ---- Stable client-side UIDs for dynamic list keys (prevents stale form state on reorder/removal) ----
  const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `uid-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  // ---- Testimonials helpers ----
  const addTestimonial = () => upd({ testimonials: [...(page.testimonials || []), { author: "", role: "", text: "", rating: 5, _uid: uid() }] });
  const updTestimonial = (idx, patch) => {
    const arr = [...(page.testimonials || [])];
    arr[idx] = { ...arr[idx], ...patch };
    upd({ testimonials: arr });
  };
  const rmTestimonial = (idx) => upd({ testimonials: (page.testimonials || []).filter((_, i) => i !== idx) });

  // ---- Services helpers ----
  const addService = () => upd({ services: [...(page.services || []), { name: "", description: "", price: "", _uid: uid() }] });
  const updService = (idx, patch) => {
    const arr = [...(page.services || [])];
    arr[idx] = { ...arr[idx], ...patch };
    upd({ services: arr });
  };
  const rmService = (idx) => upd({ services: (page.services || []).filter((_, i) => i !== idx) });

  // ---- Drag & drop reorder (testimonials + services) ----
  const reorderArray = (arr, from, to) => {
    if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
    const copy = [...arr];
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
  };
  const onDragStart = (list, idx) => (e) => {
    setDragState({ list, fromIdx: idx, overIdx: idx });
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(idx)); } catch (_) {}
  };
  const onDragOver = (list, idx) => (e) => {
    if (dragState.list !== list || dragState.fromIdx === null) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = "move"; } catch (_) {}
    if (dragState.overIdx !== idx) setDragState((s) => ({ ...s, overIdx: idx }));
  };
  const onDrop = (list, idx) => (e) => {
    e.preventDefault();
    if (dragState.list !== list || dragState.fromIdx === null) return;
    const from = dragState.fromIdx;
    setDragState({ list: null, fromIdx: null, overIdx: null });
    if (from === idx) return;
    if (list === "testimonials") {
      upd({ testimonials: reorderArray(page.testimonials || [], from, idx) });
    } else if (list === "services") {
      upd({ services: reorderArray(page.services || [], from, idx) });
    } else if (list === "gallery") {
      upd({ gallery: reorderArray(page.gallery || [], from, idx) });
    }
  };
  const onDragEnd = () => setDragState({ list: null, fromIdx: null, overIdx: null });

  // ---- Social helpers ----
  const sl = page.social_links || { facebook: "", instagram: "", linkedin: "", twitter: "", tiktok: "", youtube: "" };
  const updSocial = (k, v) => upd({ social_links: { ...sl, [k]: v } });

  // ---- Sections visibility helpers ----
  const sections = page.sections || {
    hero: true, about: true, services: true, gallery: true,
    testimonials: true, hours: true, contact: true, map: true,
  };
  const updSection = (k, v) => upd({ sections: { ...sections, [k]: !!v } });
  const setAllSections = (v) => upd({ sections: SECTION_ITEMS.reduce((acc, s) => ({ ...acc, [s.key]: v }), {}) });

  // ---- Hero slideshow helpers ----
  const slideshow = page.hero_slideshow || { enabled: true, autoplay: true, interval_ms: 5000, use_gallery: true, images: [] };
  const updSlideshow = (patch) => upd({ hero_slideshow: { ...slideshow, ...patch } });
  const slideshowImages = Array.isArray(slideshow.images) ? slideshow.images : [];
  const addSlideshowImage = (url) => updSlideshow({ images: [...slideshowImages, url] });
  const removeSlideshowImage = (i) => updSlideshow({ images: slideshowImages.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...page };
      delete payload.id;
      delete payload.user_id;
      delete payload.owner_email;
      delete payload.created_at;
      delete payload.updated_at;
      // Strip client-only _uid fields from list items before sending to backend
      if (Array.isArray(payload.testimonials)) payload.testimonials = payload.testimonials.map(({ _uid, ...rest }) => rest);
      if (Array.isArray(payload.services)) payload.services = payload.services.map(({ _uid, ...rest }) => rest);
      const res = await api.put("/business/me", payload);
      // Re-attach _uid on response so list keys stay stable after save
      const uidFn = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `uid-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const data = { ...res.data };
      if (Array.isArray(data.testimonials)) data.testimonials = data.testimonials.map((t) => (t && t._uid ? t : { ...t, _uid: uidFn() }));
      if (Array.isArray(data.services)) data.services = data.services.map((s) => (s && s._uid ? s : { ...s, _uid: uidFn() }));
      setPage(data);
      toast.success("Page enregistrée.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const generateShareLink = async () => {
    setShareLoading(true);
    setShareCopied(false);
    try {
      const res = await api.post("/business/me/preview-link");
      const fullUrl = window.location.origin + res.data.path;
      setShareLink({ url: fullUrl, expires_at: res.data.expires_at });
      setShareOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de générer le lien");
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink.url);
      setShareCopied(true);
      toast.success("Lien copié dans le presse-papiers");
      setTimeout(() => setShareCopied(false), 2200);
    } catch {
      toast.error("Impossible de copier — sélectionnez et copiez manuellement");
    }
  };

  const uploadImage = async (field) => {
    const inp = document.createElement("input");
    inp.type = "file";
    inp.accept = "image/*";
    inp.onchange = async () => {
      if (!inp.files?.[0]) return;
      setUploading(true);
      const fd = new FormData();
      fd.append("file", inp.files[0]);
      try {
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const url = `${BACKEND_URL}${res.data.url}`;
        if (field === "gallery") {
          upd({ gallery: [...(page.gallery || []), url] });
        } else if (field === "slideshow") {
          updSlideshow({ images: [...slideshowImages, url] });
        } else {
          upd({ [field]: url });
        }
        toast.success("Image ajoutée.");
      } catch (e) {
        toast.error("Échec de l'upload");
      } finally {
        setUploading(false);
      }
    };
    inp.click();
  };

  const removeGallery = (idx) => {
    upd({ gallery: page.gallery.filter((_, i) => i !== idx) });
  };

  return (
    <div className="min-h-screen bg-gray-50 font-preserve">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="container-pp min-h-16 flex flex-wrap items-center justify-between gap-y-2 py-2 sm:py-0">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Le Guide des Entrepreneurs"
              className="h-10 sm:h-12 w-auto"
              data-testid="dash-logo"
            />
            <span className="hidden md:inline text-xs text-slate-500">· Espace client · {page.business_name}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-end">
            <a href={`/entreprise/${page.slug}`} target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-md border-[#1E2A5E] text-[#1E2A5E] hover:bg-[#1E2A5E] hover:text-white font-semibold" data-testid="view-page-btn">
                <Eye className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Voir ma page</span>
              </Button>
            </a>
            <Button
              onClick={generateShareLink}
              disabled={shareLoading}
              variant="outline"
              className="rounded-md border-[#2FA398] text-[#2FA398] hover:bg-[#2FA398] hover:text-white font-semibold"
              data-testid="share-preview-btn"
            >
              <Share2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{shareLoading ? "…" : "Partager l'aperçu"}</span>
            </Button>
            <Button
              onClick={() => setPreviewOpen((v) => !v)}
              variant={previewOpen ? "default" : "outline"}
              className={previewOpen
                ? "bg-[#1E2A5E] hover:bg-[#2FA398] text-white rounded-md font-semibold"
                : "rounded-md border-[#1E2A5E] text-[#1E2A5E] hover:bg-[#1E2A5E] hover:text-white font-semibold"}
              data-testid="toggle-preview-btn"
            >
              {previewOpen ? <EyeOff className="w-4 h-4 sm:mr-2" /> : <Eye className="w-4 h-4 sm:mr-2" />}
              <span className="hidden sm:inline">{previewOpen ? "Masquer l'aperçu" : "Aperçu en direct"}</span>
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#2FA398] hover:bg-[#26857C] rounded-md font-semibold text-white" data-testid="save-btn">
              <Save className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{saving ? "Enregistrement…" : "Enregistrer"}</span>
            </Button>
            <Button variant="ghost" onClick={() => { logout(); nav("/"); }} className="rounded-md text-[#1E2A5E] font-semibold" data-testid="dash-logout">
              <span className="hidden sm:inline">Déconnexion</span>
              <span className="sm:hidden text-xs">Sortir</span>
            </Button>
          </div>
        </div>
      </header>

      <div className={previewOpen ? "max-w-[1800px] mx-auto px-5 md:px-8 py-8 grid grid-cols-1 xl:grid-cols-[1fr_minmax(360px,40%)] gap-6 items-start" : "container-pp py-10"}>
        <div className="min-w-0">
        {me?.password_reset_pending && (
          <div
            className="mb-6 p-4 border-2 border-amber-400 bg-amber-50 flex items-start gap-3 rounded-none"
            data-testid="password-reset-banner"
          >
            <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-bold text-amber-900">Mot de passe provisoire en cours d'utilisation</h3>
              <p className="text-sm text-amber-900/90 mt-0.5">
                Vous êtes connecté(e) avec un mot de passe provisoire transmis par l'administrateur.
                Pour sécuriser votre compte, choisissez dès maintenant un nouveau mot de passe.
              </p>
            </div>
            <Button
              onClick={() => setPwOpen(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-none font-semibold flex-shrink-0"
              data-testid="banner-change-password-btn"
            >
              <KeyRound className="w-4 h-4 mr-1.5" />
              Modifier maintenant
            </Button>
          </div>
        )}

        {/* Signature — the single modern template. No switcher: one polished design that adapts to every business. */}
        <Card className="rounded-2xl bg-white border border-slate-200 p-0 mb-6 overflow-hidden shadow-sm" data-testid="template-info">
          <div
            className="relative p-5 md:p-6 overflow-hidden"
            style={{ background: "linear-gradient(135deg, #1E2A5E 0%, #2FA398 100%)" }}
          >
            <span className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/10 blur-2xl pointer-events-none" />
            <span className="absolute -bottom-14 -left-14 h-52 w-52 rounded-full bg-white/10 blur-3xl pointer-events-none" />
            <div className="relative flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/15 backdrop-blur text-white shadow-md">
                  <Sparkles className="w-7 h-7" strokeWidth={2.25} />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/80">Votre modèle</div>
                  <div className="font-heading font-black text-2xl tracking-tight text-white">Signature</div>
                  <div className="text-sm text-white/85 mt-0.5">Modèle unique, moderne & animé — 100&nbsp;% personnalisable via les onglets ci-dessous.</div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Bandeau abonnement inactif */}
        {sub && !sub.active && (
          <div className="mt-6 mb-6 border border-amber-300 bg-amber-50 px-5 py-4 flex items-start justify-between gap-4 flex-wrap" data-testid="billing-alert-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-bold text-[#1E2A5E]">Aucun abonnement actif</div>
                <p className="text-sm text-amber-900/80 mt-0.5 max-w-2xl">
                  Votre page entreprise reste en ligne uniquement avec un abonnement actif à <strong>9,90&nbsp;€/mois</strong>. Souscrivez en 30 secondes, sans engagement.
                </p>
              </div>
            </div>
            <Button onClick={startSubscription} disabled={billingBusy} className="rounded-md bg-[#2FA398] hover:bg-[#26857C] text-white font-semibold" data-testid="billing-banner-subscribe">
              <CreditCard className="w-4 h-4 mr-2" />
              {billingBusy ? "Redirection…" : "Souscrire 9,90 €/mois"}
            </Button>
          </div>
        )}

        {/* Section header — tab navigation intro */}
        <div className="bg-gradient-to-r from-[#1E2A5E] to-[#2FA398] text-white border border-[#1E2A5E] -mb-px" data-testid="tabs-section-header">
          <div className="flex items-center justify-between flex-wrap gap-3 px-5 md:px-6 py-3.5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/15 backdrop-blur flex items-center justify-center font-black text-lg rounded-md">✎</div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/80">Édition de votre page</div>
                <h2 className="font-heading font-bold text-lg md:text-xl tracking-tight leading-tight">
                  Personnalisez chaque rubrique
                </h2>
              </div>
            </div>
            <div className="hidden md:block text-xs text-white/85 max-w-sm leading-snug">
              Cliquez sur une rubrique ci-dessous pour la modifier. Pensez à <strong>enregistrer</strong> en haut de page.
            </div>
          </div>
        </div>

        <Tabs defaultValue="sections" className="w-full">
          {/* Elegant tab navigation: icon + label, animated underline, horizontally scrollable on mobile */}
          <div className="bg-white border border-gray-200 -mb-px overflow-hidden">
            <TabsList
              className="rounded-none bg-transparent border-0 h-auto p-0 flex flex-nowrap overflow-x-auto w-full justify-start scrollbar-thin"
              data-testid="dash-tabs"
            >
              {DASH_TABS.map(({ value, label, hint, Icon, testid }) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className="group relative flex-shrink-0 rounded-none bg-transparent text-gray-500 hover:bg-blue-50/60 hover:text-blue-700 data-[state=active]:bg-white data-[state=active]:text-[#1E2A5E] px-3.5 py-3 md:px-4 md:py-3.5 transition-all duration-200 font-semibold text-[13px] md:text-sm flex items-center gap-2 border-b-2 border-transparent data-[state=active]:border-[#1E2A5E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E2A5E]/30"
                  data-testid={testid}
                  title={hint}
                >
                  <Icon className="w-4 h-4 transition-transform group-data-[state=active]:scale-110" strokeWidth={2.25} />
                  <span className="whitespace-nowrap">{label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="sections">
            <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8 mt-6" data-testid="sections-visibility-card">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <LayoutGrid className="w-5 h-5 text-[#1E2A5E]" />
                    <h3 className="font-heading font-bold text-2xl tracking-tight">Sections de votre page</h3>
                  </div>
                  <p className="text-sm text-gray-600 max-w-2xl">
                    Activez ou désactivez chaque bloc de votre page publique. Les sections désactivées ne s'affichent pas
                    pour vos visiteurs, mais leur contenu est <strong>conservé</strong> et reste modifiable dans les autres onglets.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setAllSections(true)}
                    className="rounded-md border-gray-300 font-semibold text-xs"
                    data-testid="sections-enable-all"
                  >
                    Tout activer
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setAllSections(false)}
                    className="rounded-md border-gray-300 font-semibold text-xs"
                    data-testid="sections-disable-all"
                  >
                    Tout désactiver
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {SECTION_ITEMS.map(({ key, label, desc }) => {
                  const active = sections[key] !== false;
                  return (
                    <div
                      key={key}
                      className={`flex items-start gap-4 p-4 border transition ${active ? "border-[#2FA398] bg-emerald-50/40" : "border-gray-200 bg-gray-50/60"}`}
                      data-testid={`section-toggle-row-${key}`}
                    >
                      <div className="pt-0.5">
                        <Switch
                          checked={active}
                          onCheckedChange={(v) => updSection(key, v)}
                          data-testid={`section-toggle-${key}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-sm text-gray-900">{label}</span>
                          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${active ? "bg-[#2FA398] text-white" : "bg-gray-300 text-gray-700"}`}>
                            {active ? "Affichée" : "Masquée"}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1 leading-relaxed">{desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 p-4 border border-blue-200 bg-blue-50/70 text-xs text-blue-900 leading-relaxed">
                <strong>Astuce :</strong> même désactivée, une section peut toujours être remplie via les autres onglets.
                Vous pouvez donc préparer un bloc à l'avance et l'activer plus tard, en un clic.
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="info">
            <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold mb-1.5 block">Nom de l'entreprise</Label>
                  <Input value={page.business_name || ""} onChange={(e) => upd({ business_name: e.target.value })} className="rounded-none border-gray-300" data-testid="field-business-name" />
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-1.5 block">Numéro SIRET</Label>
                  <Input value={page.siret || ""} onChange={(e) => upd({ siret: e.target.value })} placeholder="123 456 789 00012" className="rounded-none border-gray-300" data-testid="field-siret" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold mb-1.5 block">Domaine d'activité</Label>
                  <Input value={page.activity || ""} onChange={(e) => upd({ activity: e.target.value })} placeholder="Ex : Photographe, Restaurant, Cabinet de conseil…" className="rounded-none border-gray-300" data-testid="field-activity" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold mb-1.5 block">Description / Présentation</Label>
                  <p className="text-xs text-gray-500 mb-2">Court paragraphe affiché <strong>sous le nom de votre entreprise</strong>, en haut de votre page (accroche courte).</p>
                  <Textarea rows={4} value={page.description || ""} onChange={(e) => upd({ description: e.target.value })} placeholder="Un pitch court : votre activité et votre promesse en 2-3 phrases." className="rounded-none border-gray-300" data-testid="field-description" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold mb-1.5 block">À propos</Label>
                  <p className="text-xs text-gray-500 mb-2">Texte plus détaillé affiché dans la <strong>section « À propos / Notre histoire »</strong> de votre page (votre histoire, vos valeurs, votre équipe…).</p>
                  <Textarea rows={7} value={page.about_text || ""} onChange={(e) => upd({ about_text: e.target.value })} placeholder="Racontez votre histoire, vos valeurs, votre équipe, votre parcours…" className="rounded-none border-gray-300" data-testid="field-about-text" />
                </div>
                <div className="md:col-span-2 flex items-center gap-3 pt-2">
                  <Switch checked={!!page.published} onCheckedChange={(v) => upd({ published: v })} data-testid="field-published" />
                  <span className="text-sm font-semibold">Page publiée</span>
                  <span className="text-xs text-gray-500">— accessible publiquement à /entreprise/{page.slug}</span>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="contact">
            <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold mb-1.5 block">Adresse de l'entreprise (optionnel)</Label>
                  <Input value={page.address || ""} onChange={(e) => upd({ address: e.target.value })} placeholder="24 Rue de la République, 75011 Paris" className="rounded-none border-gray-300" data-testid="field-address" />
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-1.5 block">Téléphone</Label>
                  <Input value={page.phone || ""} onChange={(e) => upd({ phone: e.target.value })} placeholder="+33 1 23 45 67 89" className="rounded-none border-gray-300" data-testid="field-phone" />
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-1.5 block">Email professionnel</Label>
                  <Input value={page.email || ""} onChange={(e) => upd({ email: e.target.value })} placeholder="contact@votre-entreprise.fr" className="rounded-none border-gray-300" data-testid="field-email" />
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="hours">
            <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8 mt-6">
              <div className="space-y-3">
                {DAYS.map((d, i) => {
                  const oh = page.opening_hours.find((h) => h.day === d) || { day: d, open: "", close: "", closed: false };
                  const idx = page.opening_hours.findIndex((h) => h.day === d);
                  const realIdx = idx >= 0 ? idx : page.opening_hours.length;
                  return (
                    <div key={d} className="grid grid-cols-12 gap-3 items-center" data-testid={`hours-row-${d}`}>
                      <Label className="col-span-3 text-sm font-semibold">{d}</Label>
                      <div className="col-span-3">
                        <Input
                          type="time"
                          disabled={oh.closed}
                          value={oh.open}
                          onChange={(e) => setHour(realIdx, { day: d, open: e.target.value, close: oh.close, closed: false })}
                          className="rounded-none border-gray-300"
                        />
                      </div>
                      <div className="col-span-3">
                        <Input
                          type="time"
                          disabled={oh.closed}
                          value={oh.close}
                          onChange={(e) => setHour(realIdx, { day: d, open: oh.open, close: e.target.value, closed: false })}
                          className="rounded-none border-gray-300"
                        />
                      </div>
                      <div className="col-span-3 flex items-center gap-2">
                        <Switch checked={oh.closed} onCheckedChange={(v) => setHour(realIdx, { day: d, open: oh.open, close: oh.close, closed: v })} />
                        <span className="text-xs text-gray-500">Fermé</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="media">
            <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8 mt-6">
              <div>
                <Label className="text-sm font-semibold mb-3 block">Logo</Label>
                {page.logo_url ? (
                  <div className="relative inline-block">
                    <img src={page.logo_url} alt="logo" className="w-32 h-32 object-contain border border-gray-200 bg-gray-50" />
                    <button onClick={() => upd({ logo_url: "" })} className="absolute -top-2 -right-2 bg-black text-white p-1.5" data-testid="remove-logo">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <Button variant="outline" disabled={uploading} onClick={() => uploadImage("logo_url")} className="rounded-none border-black font-semibold" data-testid="upload-logo">
                    <Upload className="w-4 h-4 mr-2" />Téléverser
                  </Button>
                )}
              </div>

            </Card>
          </TabsContent>

          <TabsContent value="map">
            <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8 mt-6">
              <div className="flex items-center gap-3 mb-6">
                <Switch checked={!!page.show_map} onCheckedChange={(v) => upd({ show_map: v })} data-testid="field-show-map" />
                <span className="text-sm font-semibold">Afficher un plan sur ma page</span>
              </div>
              <Label className="text-sm font-semibold mb-1.5 block">Adresse pour la carte (Google Maps)</Label>
              <Input value={page.map_query || ""} onChange={(e) => upd({ map_query: e.target.value })} placeholder="Laissez vide pour utiliser l'adresse renseignée" className="rounded-none border-gray-300" data-testid="field-map-query" />
              {page.show_map && (page.map_query || page.address) && (
                <div className="mt-6 border border-gray-200">
                  <iframe
                    title="map-preview"
                    src={`https://www.google.com/maps?q=${encodeURIComponent(page.map_query || page.address)}&output=embed`}
                    width="100%"
                    height="320"
                    style={{ border: 0 }}
                  />
                </div>
              )}
            </Card>
          </TabsContent>

          {/* ---------- DIAPORAMA (Hero Slideshow) ---------- */}
          <TabsContent value="slideshow">
            <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8 mt-6" data-testid="slideshow-card">
              <div className="flex items-center gap-2 mb-2">
                <Play className="w-5 h-5 text-[#1E2A5E]" />
                <h3 className="font-heading font-bold text-2xl">Diaporama de la section Accueil</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6 max-w-2xl">
                Remplacez la bannière figée par un diaporama de photos qui défile automatiquement dans votre section
                d'accueil. Uploadez les images dédiées ci-dessous.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="border border-gray-200 p-4 flex items-start gap-3">
                  <Switch
                    checked={slideshow.enabled !== false}
                    onCheckedChange={(v) => updSlideshow({ enabled: !!v })}
                    data-testid="slideshow-enabled"
                  />
                  <div>
                    <div className="font-bold text-sm">Activer le diaporama</div>
                    <p className="text-xs text-gray-600 mt-1">Si désactivé, aucune image de fond n'est utilisée.</p>
                  </div>
                </div>
                <div className="border border-gray-200 p-4 flex items-start gap-3">
                  <Switch
                    checked={slideshow.autoplay !== false}
                    onCheckedChange={(v) => updSlideshow({ autoplay: !!v })}
                    data-testid="slideshow-autoplay"
                  />
                  <div>
                    <div className="font-bold text-sm">Défilement automatique</div>
                    <p className="text-xs text-gray-600 mt-1">Passe à l'image suivante toute seule.</p>
                  </div>
                </div>
              </div>

              <div className="mb-8 max-w-md">
                <Label className="text-sm font-semibold mb-2 block">
                  Intervalle entre chaque photo : <span className="text-[#1E2A5E] font-bold">{((slideshow.interval_ms || 5000) / 1000).toFixed(1)} s</span>
                </Label>
                <input
                  type="range"
                  min={1500}
                  max={12000}
                  step={500}
                  value={slideshow.interval_ms || 5000}
                  onChange={(e) => updSlideshow({ interval_ms: parseInt(e.target.value, 10) })}
                  className="w-full accent-[#2FA398]"
                  data-testid="slideshow-interval"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Rapide (1.5s)</span>
                  <span>Lent (12s)</span>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <div>
                    <h4 className="font-heading font-bold text-lg">Images dédiées au diaporama</h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Ajoutez des photos spécifiques ici (recommandé : format 4:3 ou 16:9, min. 1200 px de large).
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    disabled={uploading}
                    onClick={() => uploadImage("slideshow")}
                    className="rounded-none border-black font-semibold"
                    data-testid="upload-slideshow"
                  >
                    <Upload className="w-4 h-4 mr-2" /> Ajouter une image
                  </Button>
                </div>

                {slideshowImages.length === 0 ? (
                  <div className="border border-dashed border-gray-300 p-8 text-center bg-gray-50">
                    <ImageIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">
                      Aucune image dédiée pour l'instant. Uploadez au moins une photo pour activer le diaporama.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {slideshowImages.map((url, i) => (
                      <div key={i} className="relative aspect-square border border-gray-200" data-testid={`slideshow-tile-${i}`}>
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => removeSlideshowImage(i)}
                          className="absolute top-1 right-1 bg-black text-white p-1"
                          data-testid={`slideshow-remove-${i}`}
                          aria-label="Supprimer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <span className="absolute bottom-1 left-1 text-[10px] font-bold px-1.5 py-0.5 bg-black/60 text-white rounded">#{i + 1}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>


          {/* ---------- TÉMOIGNAGES ---------- */}
          <TabsContent value="testimonials">
            <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8 mt-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading font-bold text-2xl mb-1">Témoignages clients</h3>
                  <p className="text-sm text-gray-600">Affichez les avis de vos meilleurs clients sur votre page. <span className="hidden md:inline text-gray-400">· Glissez la poignée <GripVertical className="inline w-3.5 h-3.5 -translate-y-0.5" /> pour réordonner</span></p>
                </div>
                <Button onClick={addTestimonial} className="bg-blue-600 hover:bg-blue-700 rounded-md font-semibold" data-testid="add-testimonial">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter
                </Button>
              </div>

              <div className="space-y-4">
                {(page.testimonials || []).length === 0 && (
                  <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-200">
                    Aucun témoignage pour l'instant. Cliquez sur « Ajouter ».
                  </div>
                )}
                {(page.testimonials || []).map((t, i) => {
                  const isDragging = dragState.list === "testimonials" && dragState.fromIdx === i;
                  const isOver = dragState.list === "testimonials" && dragState.overIdx === i && dragState.fromIdx !== i;
                  return (
                  <div
                    key={t._uid || `t-${i}`}
                    onDragOver={onDragOver("testimonials", i)}
                    onDrop={onDrop("testimonials", i)}
                    className={`border p-5 pl-12 relative transition ${isDragging ? "opacity-40 border-blue-300" : isOver ? "border-blue-500 bg-blue-50/40" : "border-gray-200"}`}
                    data-testid={`testimonial-row-${i}`}
                  >
                    <button
                      type="button"
                      draggable
                      onDragStart={onDragStart("testimonials", i)}
                      onDragEnd={onDragEnd}
                      title="Glissez pour réordonner"
                      aria-label={`Réordonner le témoignage ${i + 1}`}
                      className="absolute top-1/2 -translate-y-1/2 left-2 p-1 text-gray-300 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                      data-testid={`drag-testimonial-${i}`}
                    >
                      <GripVertical className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => rmTestimonial(i)}
                      className="absolute top-3 right-3 text-gray-400 hover:text-red-500"
                      data-testid={`rm-testimonial-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-semibold mb-1 block uppercase tracking-wider text-gray-500">Auteur</Label>
                        <Input value={t.author || ""} onChange={(e) => updTestimonial(i, { author: e.target.value })} className="rounded-none border-gray-300" data-testid={`testimonial-author-${i}`} placeholder="Marie L." />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold mb-1 block uppercase tracking-wider text-gray-500">Rôle / entreprise</Label>
                        <Input value={t.role || ""} onChange={(e) => updTestimonial(i, { role: e.target.value })} className="rounded-none border-gray-300" data-testid={`testimonial-role-${i}`} placeholder="Boulangerie du Moulin" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs font-semibold mb-1 block uppercase tracking-wider text-gray-500">Témoignage</Label>
                        <Textarea rows={3} value={t.text || ""} onChange={(e) => updTestimonial(i, { text: e.target.value })} className="rounded-none border-gray-300" data-testid={`testimonial-text-${i}`} placeholder="Service rapide et professionnel..." />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold mb-1 block uppercase tracking-wider text-gray-500">Note</Label>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button
                              key={n}
                              type="button"
                              onClick={() => updTestimonial(i, { rating: n })}
                              data-testid={`testimonial-rating-${i}-${n}`}
                              className="p-1"
                            >
                              <Star className={`w-5 h-5 ${n <= (t.rating || 0) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </Card>
          </TabsContent>

          {/* ---------- SERVICES / MENU ---------- */}
          <TabsContent value="services">
            <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8 mt-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading font-bold text-2xl mb-1">Services / Menu</h3>
                  <p className="text-sm text-gray-600">Listez vos prestations ou plats. Idéal pour les modèles Restaurant, Boutique, Artisan, Services. <span className="hidden md:inline text-gray-400">· Glissez la poignée <GripVertical className="inline w-3.5 h-3.5 -translate-y-0.5" /> pour réordonner</span></p>
                </div>
                <Button onClick={addService} className="bg-blue-600 hover:bg-blue-700 rounded-md font-semibold" data-testid="add-service">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter
                </Button>
              </div>

              <div className="space-y-4">
                {(page.services || []).length === 0 && (
                  <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-200">
                    Aucun service pour l'instant. Cliquez sur « Ajouter ».
                  </div>
                )}
                {(page.services || []).map((s, i) => {
                  const isDragging = dragState.list === "services" && dragState.fromIdx === i;
                  const isOver = dragState.list === "services" && dragState.overIdx === i && dragState.fromIdx !== i;
                  return (
                  <div
                    key={s._uid || `s-${i}`}
                    onDragOver={onDragOver("services", i)}
                    onDrop={onDrop("services", i)}
                    className={`border p-5 pl-12 relative transition ${isDragging ? "opacity-40 border-blue-300" : isOver ? "border-blue-500 bg-blue-50/40" : "border-gray-200"}`}
                    data-testid={`service-row-${i}`}
                  >
                    <button
                      type="button"
                      draggable
                      onDragStart={onDragStart("services", i)}
                      onDragEnd={onDragEnd}
                      title="Glissez pour réordonner"
                      aria-label={`Réordonner le service ${i + 1}`}
                      className="absolute top-1/2 -translate-y-1/2 left-2 p-1 text-gray-300 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                      data-testid={`drag-service-${i}`}
                    >
                      <GripVertical className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => rmService(i)}
                      className="absolute top-3 right-3 text-gray-400 hover:text-red-500"
                      data-testid={`rm-service-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-5">
                        <Label className="text-xs font-semibold mb-1 block uppercase tracking-wider text-gray-500">Nom</Label>
                        <Input value={s.name || ""} onChange={(e) => updService(i, { name: e.target.value })} className="rounded-none border-gray-300" data-testid={`service-name-${i}`} placeholder="Coupe / Couleur" />
                      </div>
                      <div className="md:col-span-4">
                        <Label className="text-xs font-semibold mb-1 block uppercase tracking-wider text-gray-500">Description</Label>
                        <Input value={s.description || ""} onChange={(e) => updService(i, { description: e.target.value })} className="rounded-none border-gray-300" data-testid={`service-desc-${i}`} placeholder="Brève description" />
                      </div>
                      <div className="md:col-span-3">
                        <Label className="text-xs font-semibold mb-1 block uppercase tracking-wider text-gray-500">Prix</Label>
                        <Input value={s.price || ""} onChange={(e) => updService(i, { price: e.target.value })} className="rounded-none border-gray-300" data-testid={`service-price-${i}`} placeholder="À partir de 50€" />
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </Card>
          </TabsContent>

          {/* ---------- RÉSEAUX SOCIAUX & CTA ---------- */}
          <TabsContent value="social">
            <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8 mt-6">
              <h3 className="font-heading font-bold text-2xl mb-1">Réseaux sociaux</h3>
              <p className="text-sm text-gray-600 mb-6">Affichez les liens vers vos profils. Laissez vide pour masquer.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { k: "facebook", label: "Facebook", icon: Facebook, ph: "https://facebook.com/votre-page" },
                  { k: "instagram", label: "Instagram", icon: Instagram, ph: "https://instagram.com/votre-compte" },
                  { k: "linkedin", label: "LinkedIn", icon: Linkedin, ph: "https://linkedin.com/company/..." },
                  { k: "twitter", label: "X / Twitter", icon: Twitter, ph: "https://x.com/..." },
                  { k: "tiktok", label: "TikTok", icon: Music2, ph: "https://tiktok.com/@..." },
                  { k: "youtube", label: "YouTube", icon: Youtube, ph: "https://youtube.com/@..." },
                ].map(({ k, label, icon: Ic, ph }) => (
                  <div key={k}>
                    <Label className="text-sm font-semibold mb-1.5 flex items-center gap-2"><Ic className="w-4 h-4 text-blue-600" />{label}</Label>
                    <Input
                      value={sl[k] || ""}
                      onChange={(e) => updSocial(k, e.target.value)}
                      placeholder={ph}
                      className="rounded-none border-gray-300"
                      data-testid={`social-${k}`}
                    />
                  </div>
                ))}
              </div>

              <div className="h-px bg-gray-200 my-8" />

              <h3 className="font-heading font-bold text-2xl mb-1">Bouton d'action personnalisé (CTA)</h3>
              <p className="text-sm text-gray-600 mb-6">Affichez un bouton bien visible (ex. « Réserver », « Demander un devis »).</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold mb-1.5 block">Texte du bouton</Label>
                  <Input value={page.cta_text || ""} onChange={(e) => upd({ cta_text: e.target.value })} placeholder="Réserver maintenant" className="rounded-none border-gray-300" data-testid="field-cta-text" />
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-1.5 block">Lien / URL</Label>
                  <Input value={page.cta_url || ""} onChange={(e) => upd({ cta_url: e.target.value })} placeholder="https://… ou mailto:… ou tel:…" className="rounded-none border-gray-300" data-testid="field-cta-url" />
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="invoices">
            <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8 mt-6" data-testid="invoices-card">              <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Receipt className="w-5 h-5 text-blue-600" />
                    <h2 className="font-heading font-bold text-2xl tracking-tight">Ma facture</h2>
                  </div>
                  <p className="text-sm text-gray-600 max-w-2xl">
                    Votre facture est générée au format <strong>Factur-X</strong> (PDF/A-3 conforme à la facturation électronique française, avec XML structuré embarqué). TVA non applicable&nbsp;— art. 293 B du CGI.
                  </p>
                </div>
              </div>
              {invoicesLoading ? (
                <div className="text-gray-500 text-sm py-8 text-center">Chargement…</div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-gray-300 bg-gray-50" data-testid="invoices-empty">
                  <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 text-sm">Aucune facture pour le moment.</p>
                  <p className="text-gray-500 text-xs mt-1">Une facture est générée automatiquement après chaque paiement.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr className="text-left">
                        <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">N° Facture</th>
                        <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">Date</th>
                        <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">Montant TTC</th>
                        <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">Statut</th>
                        <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest text-right">Téléchargement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`invoice-row-${inv.number}`}>
                          <td className="px-3 py-3 font-mono font-semibold text-gray-900">{inv.number}</td>
                          <td className="px-3 py-3 text-gray-600">{(inv.issued_at || "").slice(0, 10)}</td>
                          <td className="px-3 py-3 font-semibold">{Number(inv.amount_ttc || 0).toFixed(2).replace(".", ",")} €</td>
                          <td className="px-3 py-3">
                            <span className="text-xs font-bold px-2 py-1 bg-emerald-100 text-emerald-700">Payée</span>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <Button
                              size="sm"
                              onClick={() => downloadInvoice(inv)}
                              disabled={downloadingInvoiceId === inv.id}
                              className="rounded-none bg-[#1E2A5E] hover:bg-[#2FA398] text-white font-semibold"
                              data-testid={`download-invoice-${inv.number}`}
                            >
                              <Download className="w-4 h-4 mr-1.5" />
                              {downloadingInvoiceId === inv.id ? "Téléchargement…" : "PDF Factur-X"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Partenaires de facturation */}
            <Card className="rounded-none bg-gradient-to-br from-blue-50 to-emerald-50 border border-blue-200 p-6 md:p-8 mt-6" data-testid="invoice-partners-card">
              <div className="flex items-center gap-2 mb-2">
                <Receipt className="w-5 h-5 text-blue-600" />
                <h3 className="font-heading font-bold text-xl tracking-tight">Aller plus loin avec un logiciel partenaire</h3>
              </div>
              <p className="text-sm text-gray-700 max-w-2xl mb-5">
                Besoin d'émettre <strong>plus de facture</strong> (devis, facture récurrente, multi-clients, multi-TVA…) ? Ces outils sont compatibles avec votre activité et avec la facturation électronique française.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <a
                  href="https://www.dougs.fr/logiciel-facturation-gratuit/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { api.post("/track/partner-click", { partner: "dougs" }).catch(() => {}); }}
                  className="group block p-5 bg-white border border-gray-200 hover:border-blue-600 hover:shadow-md transition"
                  data-testid="invoice-partner-dougs"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-1">Facturation électronique agréée</div>
                      <div className="font-bold text-gray-900 group-hover:text-blue-600 transition">
                        Essayer la facture électronique agréée avec www.dougs.fr
                      </div>
                      <div className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                        dougs.fr <ExternalLink className="w-3 h-3" />
                      </div>
                    </div>
                  </div>
                </a>
                <a
                  href="https://www.mafactureentreprise.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { api.post("/track/partner-click", { partner: "mafactureentreprise" }).catch(() => {}); }}
                  className="group block p-5 bg-white border border-gray-200 hover:border-emerald-600 hover:shadow-md transition"
                  data-testid="invoice-partner-mafactureentreprise"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-emerald-600 flex items-center justify-center flex-shrink-0">
                      <Receipt className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-1">Générateur de facture compatible</div>
                      <div className="font-bold text-gray-900 group-hover:text-emerald-700 transition">
                        Essayer le générateur de facture compatible avec www.MaFactureEntreprise.fr
                      </div>
                      <div className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                        mafactureentreprise.fr <ExternalLink className="w-3 h-3" />
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="billing">
            <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8 mt-6" data-testid="billing-card">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-5 h-5 text-[#2FA398]" />
                <h2 className="font-heading font-bold text-2xl tracking-tight">Mon abonnement</h2>
              </div>
              <p className="text-sm text-gray-600 max-w-2xl">
                {sub?.free_access ? (
                  <>Votre compte bénéficie d&apos;un <strong>accès offert et illimité</strong> : aucun abonnement, aucun prélèvement. Votre page reste en ligne sans condition.</>
                ) : (
                  <>Votre page professionnelle est incluse dans un abonnement de <strong>9,90&nbsp;€ TTC / mois</strong>, sans engagement.
                  Vous pouvez résilier à tout moment depuis le portail sécurisé Stripe : votre page reste en ligne jusqu&apos;à la fin de la période payée.</>
                )}
              </p>

              {subLoading ? (
                <div className="text-gray-500 text-sm py-8">Chargement…</div>
              ) : (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border border-gray-200 p-5">
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Statut</div>
                    <div className="mt-2 font-bold text-lg" data-testid="billing-status">
                      {sub?.free_access ? (
                        <span className="text-[#2FA398]">Compte offert</span>
                      ) : sub?.active ? (
                        <span className="text-emerald-600">Actif</span>
                      ) : sub?.status === "past_due" ? (
                        <span className="text-amber-600">Paiement en échec</span>
                      ) : sub?.status === "canceled" ? (
                        <span className="text-red-600">Résilié</span>
                      ) : (
                        <span className="text-gray-500">Aucun abonnement</span>
                      )}
                    </div>
                  </div>
                  <div className="border border-gray-200 p-5">
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Tarif</div>
                    <div className="mt-2 font-bold text-lg text-[#1E2A5E]">{sub?.free_access ? "0 € — accès offert" : "9,90 € / mois"}</div>
                  </div>
                  <div className="border border-gray-200 p-5">
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Prochaine échéance</div>
                    <div className="mt-2 font-bold text-lg text-[#1E2A5E]" data-testid="billing-period-end">
                      {sub?.current_period_end
                        ? String(sub.current_period_end).slice(0, 10)
                        : sub?.details_unavailable
                          ? <span className="text-sm font-semibold text-gray-500">Visible depuis le portail Stripe</span>
                          : "—"}
                    </div>
                  </div>
                </div>
              )}

              {sub?.cancel_at_period_end && (
                <p className="mt-4 text-sm text-amber-700" data-testid="billing-cancel-notice">
                  Résiliation programmée : votre abonnement s&apos;arrêtera à la fin de la période en cours.
                </p>
              )}

              <div className="mt-6 flex items-center gap-3 flex-wrap">
                {sub?.can_manage && (
                  <Button onClick={openBillingPortal} disabled={billingBusy} className="rounded-md bg-[#1E2A5E] hover:bg-[#2A3B7A] text-white font-semibold" data-testid="billing-portal-btn">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {billingBusy ? "Redirection…" : "Gérer mon abonnement"}
                  </Button>
                )}
                {!sub?.active && (
                  <Button onClick={startSubscription} disabled={billingBusy} className="rounded-md bg-[#2FA398] hover:bg-[#26857C] text-white font-semibold" data-testid="billing-subscribe-btn">
                    <CreditCard className="w-4 h-4 mr-2" />
                    {billingBusy ? "Redirection…" : "Souscrire 9,90 €/mois"}
                  </Button>
                )}
              </div>
              {!sub?.free_access && (
                <p className="mt-4 text-xs text-gray-500 max-w-2xl">
                  Le portail « Gérer mon abonnement » est hébergé par Stripe : vous y modifiez votre carte bancaire, téléchargez vos reçus et résiliez en un clic.
                </p>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
          <a href={`/entreprise/${page.slug}`} target="_blank" rel="noreferrer" className="text-blue-600 font-semibold flex items-center gap-2 hover:underline">
            <ExternalLink className="w-4 h-4" />
            Prévisualiser ma page publique
          </a>
          <Button onClick={save} disabled={saving} className="rounded-md bg-emerald-500 hover:bg-emerald-600 text-white py-6 px-8 font-semibold" data-testid="save-btn-bottom">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Enregistrement…" : "Enregistrer les modifications"}
          </Button>
        </div>

        {/* Optimized social-share link (OpenGraph preview) — only when published — placed at the bottom of the editor */}
        {page.published && page.slug && (() => {
          const socialUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/api/og/${page.slug}`;
          const shareText = `Découvrez ${page.business_name || "ma page"} !`;
          const copySocial = () => {
            navigator.clipboard.writeText(socialUrl);
            setSocialCopied(true);
            setTimeout(() => setSocialCopied(false), 2000);
            toast.success("Lien de partage social copié");
          };
          return (
            <Card className="rounded-none bg-gradient-to-br from-blue-50 to-emerald-50 border border-blue-200 p-5 mt-8" data-testid="social-share-card">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2 mb-1">
                    <Share2 className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-bold uppercase tracking-widest text-blue-700">Lien partage social</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">
                    Utilisez ce lien pour partager votre page sur Facebook, WhatsApp, LinkedIn… il affichera un <strong>bel aperçu</strong> avec votre image principale, votre nom et votre description.
                  </p>
                  <div className="flex items-stretch gap-2 mb-3">
                    <Input
                      readOnly
                      value={socialUrl}
                      onFocus={(e) => e.target.select()}
                      className="rounded-md border-gray-300 font-mono text-xs bg-white"
                      data-testid="social-share-url"
                    />
                    <Button
                      onClick={copySocial}
                      className={socialCopied ? "bg-emerald-600 hover:bg-emerald-700 rounded-md" : "bg-blue-600 hover:bg-blue-700 rounded-md"}
                      data-testid="social-share-copy"
                    >
                      {socialCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(socialUrl)}`}
                      target="_blank" rel="noreferrer"
                      data-testid="social-share-facebook"
                    >
                      <Button variant="outline" size="sm" className="rounded-md border-gray-300 font-semibold gap-2">
                        <Facebook className="w-4 h-4" /> Facebook
                      </Button>
                    </a>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + socialUrl)}`}
                      target="_blank" rel="noreferrer"
                      data-testid="social-share-whatsapp"
                    >
                      <Button variant="outline" size="sm" className="rounded-md border-gray-300 font-semibold gap-2">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981z"/></svg>
                        WhatsApp
                      </Button>
                    </a>
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(socialUrl)}`}
                      target="_blank" rel="noreferrer"
                      data-testid="social-share-linkedin"
                    >
                      <Button variant="outline" size="sm" className="rounded-md border-gray-300 font-semibold gap-2">
                        <Linkedin className="w-4 h-4" /> LinkedIn
                      </Button>
                    </a>
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(socialUrl)}`}
                      target="_blank" rel="noreferrer"
                      data-testid="social-share-twitter"
                    >
                      <Button variant="outline" size="sm" className="rounded-md border-gray-300 font-semibold gap-2">
                        <Twitter className="w-4 h-4" /> X / Twitter
                      </Button>
                    </a>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-3">
                    L'image utilisée est votre <strong>logo</strong>. Elle apparaît en aperçu lors du partage.
                  </p>
                </div>
              </div>
            </Card>
          );
        })()}

        {/* Security — change password */}
        <Card className="rounded-none bg-white border-2 border-blue-100 mt-6 overflow-hidden" data-testid="security-card">
          <div className="px-5 md:px-6 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-blue-700" />
            <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-blue-800">Sécurité du compte</h3>
          </div>
          <div className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            <div className="md:col-span-2">
              <h4 className="font-heading font-bold text-lg text-gray-900 mb-1">Mot de passe</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Modifiez votre mot de passe à tout moment. Si vous avez reçu un mot de passe
                provisoire de la part de l'administrateur après avoir cliqué sur «&nbsp;Mot de
                passe oublié&nbsp;?&nbsp;», choisissez ici votre nouveau mot de passe personnel.
              </p>
              <ul className="mt-3 text-xs text-gray-600 space-y-1 list-disc pl-5">
                <li>Minimum&nbsp;<strong>8 caractères</strong>.</li>
                <li>Préférez un mot de passe différent de ceux utilisés ailleurs.</li>
              </ul>
            </div>
            <div className="md:text-right">
              <Button
                onClick={() => setPwOpen(true)}
                variant="outline"
                className="rounded-none border-2 border-blue-600 text-blue-700 hover:bg-blue-600 hover:text-white font-semibold w-full md:w-auto"
                data-testid="open-change-password"
              >
                <KeyRound className="w-4 h-4 mr-1.5" />
                Modifier le mot de passe
              </Button>
            </div>
          </div>
        </Card>

        {/* Danger zone — soft-delete account */}
        <Card className="rounded-none bg-white border-2 border-red-200 mt-6 overflow-hidden" data-testid="danger-zone-card">
          <div className="px-5 md:px-6 py-3 bg-red-50 border-b border-red-200 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-red-700" />
            <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-red-800">Zone sensible</h3>
          </div>
          <div className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            <div className="md:col-span-2">
              <h4 className="font-heading font-bold text-lg text-gray-900 mb-1">Supprimer mon compte</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Votre compte sera <strong>désactivé immédiatement</strong> : votre page publique deviendra inaccessible et vous serez déconnecté(e).
              </p>
              <div className="mt-3 p-3 bg-emerald-50 border-l-4 border-emerald-500 text-sm text-emerald-900 leading-snug">
                <strong className="font-bold">Récupération gratuite à tout moment.</strong> Pour réactiver votre compte, il vous suffira de vous reconnecter avec votre email et votre mot de passe. Votre page, votre contenu et votre facture seront <strong>intégralement restaurés</strong>. <span className="text-emerald-800/80">Aucun frais supplémentaire.</span>
              </div>
              <ul className="mt-3 text-xs text-gray-600 space-y-1 list-disc pl-5">
                <li>Votre <strong>facture</strong> reste conservée (obligation légale française&nbsp;: 10&nbsp;ans).</li>
                <li>Votre <strong>page</strong> et tous vos contenus sont préservés en sommeil.</li>
                <li>La <strong>réactivation</strong> est automatique à la prochaine connexion réussie.</li>
              </ul>
            </div>
            <div className="md:text-right">
              <Button
                onClick={() => setDeleteOpen(true)}
                variant="outline"
                className="rounded-none border-2 border-red-600 text-red-700 hover:bg-red-600 hover:text-white font-semibold w-full md:w-auto"
                data-testid="open-delete-account"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                Supprimer mon compte
              </Button>
            </div>
          </div>
        </Card>
        </div>

        {previewOpen && (
          <aside className="hidden xl:block sticky top-[88px] self-start" data-testid="live-preview">
            <LivePreviewPanel page={page} device={device} setDevice={setDevice} />
          </aside>
        )}
      </div>

      {previewOpen && (
        <div className="xl:hidden fixed bottom-4 right-4 z-40">
          <Button
            onClick={() => setPreviewOpen(false)}
            className="rounded-full shadow-xl bg-blue-600 hover:bg-blue-700"
            data-testid="close-preview-mobile"
          >
            <EyeOff className="w-4 h-4 mr-2" /> Fermer l'aperçu
          </Button>
        </div>
      )}

      {/* Share preview dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-600" />
              Partager un aperçu privé
            </DialogTitle>
            <DialogDescription>
              Envoyez ce lien à un proche pour qu'il valide votre page avant publication.
              Le lien fonctionne même si votre page n'est pas encore publique.
            </DialogDescription>
          </DialogHeader>

          {shareLink && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 block">Lien temporaire</Label>
                <div className="flex items-stretch gap-2">
                  <Input
                    readOnly
                    value={shareLink.url}
                    className="rounded-md border-gray-300 font-mono text-xs"
                    onFocus={(e) => e.target.select()}
                    data-testid="share-link-input"
                  />
                  <Button
                    onClick={copyShareLink}
                    className={shareCopied ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}
                    data-testid="share-copy-btn"
                  >
                    {shareCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-md">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span>
                  Lien valide pendant <strong>24h</strong>, expire le{" "}
                  <span className="font-mono">
                    {new Date(shareLink.expires_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                </span>
              </div>

              <div className="flex flex-wrap gap-2">
                <a
                  href={`mailto:?subject=${encodeURIComponent("Aperçu de ma page " + (page.business_name || ""))}&body=${encodeURIComponent("Bonjour,\n\nVoici un aperçu privé de ma page web :\n" + shareLink.url + "\n\nLien valide 24h. Merci pour ton avis !")}`}
                  className="flex-1 min-w-[160px]"
                >
                  <Button variant="outline" className="w-full rounded-md border-gray-300 font-semibold gap-2" data-testid="share-email">
                    <Mail className="w-4 h-4" /> E-mail
                  </Button>
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent("Aperçu privé de ma page : " + shareLink.url)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 min-w-[160px]"
                >
                  <Button variant="outline" className="w-full rounded-md border-gray-300 font-semibold gap-2" data-testid="share-whatsapp">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981z"/></svg>
                    WhatsApp
                  </Button>
                </a>
              </div>

              <p className="text-xs text-gray-500">
                Ce lien donne un accès <strong>en lecture seule</strong> à votre page. Aucune information sensible n'est exposée.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={generateShareLink}
              disabled={shareLoading}
              variant="ghost"
              className="text-blue-600 hover:bg-blue-50"
              data-testid="share-regenerate"
            >
              Regénérer un nouveau lien
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Change password dialog */}
      <Dialog open={pwOpen} onOpenChange={(o) => { setPwOpen(o); if (!o) setPwForm({ current_password: "", new_password: "", confirm_password: "" }); }}>
        <DialogContent className="sm:max-w-md rounded-none" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-800">
              <KeyRound className="w-5 h-5" />
              {me?.password_reset_pending ? "Choisir un nouveau mot de passe" : "Modifier le mot de passe"}
            </DialogTitle>
            <DialogDescription className="pt-2 text-gray-700">
              {me?.password_reset_pending
                ? "Saisissez le mot de passe provisoire reçu de l'administrateur, puis votre nouveau mot de passe personnel."
                : "Saisissez votre mot de passe actuel, puis votre nouveau mot de passe."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={changePassword} className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">
                {me?.password_reset_pending ? "Mot de passe provisoire" : "Mot de passe actuel"}
              </Label>
              <Input
                type="password"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                required
                className="rounded-none border-gray-300"
                data-testid="pw-current"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">Nouveau mot de passe</Label>
              <Input
                type="password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                required
                minLength={8}
                className="rounded-none border-gray-300"
                data-testid="pw-new"
              />
              <p className="text-xs text-gray-500 mt-1">8 caractères minimum.</p>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">Confirmer le nouveau mot de passe</Label>
              <Input
                type="password"
                value={pwForm.confirm_password}
                onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                required
                minLength={8}
                className="rounded-none border-gray-300"
                data-testid="pw-confirm"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPwOpen(false)}
                className="rounded-none"
                data-testid="pw-cancel-btn"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={pwSaving || !pwForm.current_password || pwForm.new_password.length < 8 || pwForm.new_password !== pwForm.confirm_password}
                className="rounded-none bg-[#1E2A5E] hover:bg-[#2FA398] text-white font-semibold disabled:opacity-50"
                data-testid="pw-submit-btn"
              >
                {pwSaving ? "Modification…" : "Enregistrer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete account confirmation dialog */}
      <Dialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) setDeleteConfirm(""); }}>
        <DialogContent className="sm:max-w-lg" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Supprimer votre compte ?
            </DialogTitle>
            <DialogDescription className="pt-2 text-gray-700">
              Votre compte sera <strong>désactivé</strong> et votre page publique deviendra inaccessible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-3 text-sm text-emerald-900">
              <div className="font-bold mb-1">✓ Récupération gratuite à tout moment</div>
              Reconnectez-vous avec votre email et votre mot de passe&nbsp;: votre compte, votre page et votre facture seront <strong>intégralement restaurés</strong>.
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1.5 block text-red-700">
                Pour confirmer, tapez <code className="bg-red-100 px-1.5 py-0.5 font-mono font-bold">SUPPRIMER</code> ci-dessous
              </Label>
              <Input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="SUPPRIMER"
                className="rounded-none border-red-300 font-mono uppercase"
                data-testid="delete-confirm-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setDeleteOpen(false); setDeleteConfirm(""); }}
              className="rounded-none"
              data-testid="delete-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={deleteAccount}
              disabled={deleting || deleteConfirm.trim().toUpperCase() !== "SUPPRIMER"}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50"
              data-testid="delete-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              {deleting ? "Suppression…" : "Désactiver mon compte"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ---------- Live Preview Panel ---------- */
const LivePreviewPanel = ({ page, device, setDevice }) => {
  const { w, h, label } = DEVICES[device] || DEVICES.desktop;
  // Cap container width on screen; scale preview down via transform
  const containerMaxW = 720; // px — visible width on screen
  const scale = useMemo(() => {
    if (w <= containerMaxW) return 1;
    return Math.min(1, containerMaxW / w);
  }, [w]);
  const scaledH = h * scale;

  return (
    <div className="bg-white border border-gray-200 rounded-md overflow-hidden shadow-sm">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          </div>
          <span className="text-xs text-gray-500 font-mono ml-3 truncate max-w-[180px]">
            /entreprise/{page.slug}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {Object.entries(DEVICES).map(([k, d]) => (
            <button
              key={k}
              type="button"
              onClick={() => setDevice(k)}
              title={d.label}
              className={`p-1.5 rounded transition ${device === k ? "bg-blue-600 text-white" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`}
              data-testid={`preview-device-${k}`}
            >
              <d.Icon className="w-4 h-4" />
            </button>
          ))}
        </div>
      </div>
      {/* Preview frame */}
      <div className="bg-gray-100 p-4 flex items-start justify-center overflow-hidden" data-testid="preview-frame">
        <div
          style={{
            width: w * scale,
            height: Math.min(scaledH, 760),
            overflow: "hidden",
            border: "1px solid #E5E7EB",
            background: "white",
            borderRadius: 4,
          }}
        >
          <div
            style={{
              width: w,
              height: h,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              overflowY: "auto",
              overflowX: "hidden",
              pointerEvents: "auto",
            }}
          >
            <TemplateRenderer page={page} />
          </div>
        </div>
      </div>
      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-[11px] text-gray-500 flex items-center justify-between">
        <span>Aperçu live · {label} · {w}×{h}</span>
        <span className="text-gray-400">Non sauvegardé tant que vous n'avez pas cliqué « Enregistrer »</span>
      </div>
    </div>
  );
};

export default Dashboard;
